import { useMutation } from "@apollo/client";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { LoadingButton } from "@mui/lab";
import { Button, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { RightDrawerShell } from "components/RightDrawerShell/RightDrawerShell";
import { UpdateStateWasteStreamRegistrationInput } from "generated-graphql/graphql";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";

import { UPDATE_STATE_WASTE_STREAM_REGISTRATION } from "./api";
import { RegistrationRow, STATUS_SELECT_ITEMS } from "./columns";
import { RegistrationFormFields, RegistrationFormValues } from "./RegistrationForm";
import { statusLabel } from "./StatusBadge";

function toFormValues(registration: RegistrationRow): RegistrationFormValues {
  return {
    // Edit doesn't use the facility field (it renders the header instead); the
    // shared form shape carries it so create and edit share RegistrationFormFields.
    facility: null,
    stateRegisteredId: registration.stateRegisteredId ?? "",
    status: registration.status,
    name: registration.name ?? "",
    federalWasteCodes: registration.federalWasteCodes,
    formCodes: registration.formCodes,
    sourceCode: registration.sourceCode ?? "",
    dotId: registration.dotId ?? "",
    dotDescription: registration.dotDescription ?? "",
    registeredAt: registration.registeredAt ?? null,
    closedAt: registration.closedAt ?? null,
  };
}

// Placeholder defaults for when no registration is selected (drawer closed).
// `reset` swaps these out for the real row's values the instant one opens.
const EMPTY_FORM_VALUES: RegistrationFormValues = {
  facility: null,
  stateRegisteredId: "",
  status: STATUS_SELECT_ITEMS[0].value,
  name: "",
  federalWasteCodes: [],
  formCodes: [],
  sourceCode: "",
  dotId: "",
  dotDescription: "",
  registeredAt: null,
  closedAt: null,
};

type Props = {
  registration: RegistrationRow | null;
  onClose: () => void;
};

/**
 * Edit drawer for a single state waste stream registration. Only the fields
 * the user actually touched are sent to the update mutation (react-hook-form
 * dirtyFields) — a partial update leaves everything else untouched server-side.
 */
export function EditRegistrationDrawer({ registration, onClose }: Props) {
  const open = registration !== null;
  const alerts = useAlerts();

  const { control, handleSubmit, reset, formState } = useForm<RegistrationFormValues>({
    defaultValues: EMPTY_FORM_VALUES,
  });

  // A different row (or the drawer closing) resets the form to that row's own
  // values, establishing a fresh dirty-tracking baseline.
  useEffect(() => {
    reset(registration ? toFormValues(registration) : EMPTY_FORM_VALUES);
  }, [registration, reset]);

  const [updateRegistration, { loading }] = useMutation(UPDATE_STATE_WASTE_STREAM_REGISTRATION, {
    refetchQueries: ["StateWasteStreamRegistrations"],
    awaitRefetchQueries: true,
  });

  const onSubmit = async (data: RegistrationFormValues) => {
    if (!registration) return;
    const dirty = formState.dirtyFields;
    const input: UpdateStateWasteStreamRegistrationInput = { id: registration.id };
    if (dirty.stateRegisteredId) input.stateRegisteredId = data.stateRegisteredId.trim() || null;
    if (dirty.status) input.status = data.status;
    if (dirty.name) input.name = data.name.trim() || null;
    if (dirty.federalWasteCodes) input.federalWasteCodes = data.federalWasteCodes;
    if (dirty.formCodes) input.formCodes = data.formCodes;
    if (dirty.sourceCode) input.sourceCode = data.sourceCode.trim() || null;
    if (dirty.dotId) input.dotId = data.dotId.trim() || null;
    if (dirty.dotDescription) input.dotDescription = data.dotDescription.trim() || null;
    if (dirty.registeredAt) input.registeredAt = data.registeredAt;
    if (dirty.closedAt) input.closedAt = data.closedAt;

    try {
      await updateRegistration({ variables: { input } });
      alerts.success("Registration updated.");
      onClose();
    } catch (e) {
      alerts.error("Failed to update the registration.", e);
    }
  };

  const footer = registration ? (
    <Stack direction="row" spacing={1} justifyContent="flex-end">
      <Button onClick={onClose} disabled={loading}>
        Cancel
      </Button>
      <LoadingButton
        variant="contained"
        loading={loading}
        disabled={loading || !formState.isDirty}
        onClick={handleSubmit(onSubmit)}
      >
        Save
      </LoadingButton>
    </Stack>
  ) : null;

  return (
    <RightDrawerShell
      open={open}
      onClose={onClose}
      title={registration?.name || registration?.facility?.name || registration?.state || ""}
      footer={footer}
    >
      {registration && (
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              {registration.tenantName} · {registration.state} · {statusLabel(registration.status)}
            </Typography>
            {registration.facility && (
              <Link
                to={`/o/${registration.tenantId}/waste/facilities/${registration.facilityId}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
              >
                <OpenInNew fontSize="small" />
                {registration.facility.name}
              </Link>
            )}
            <Typography variant="caption" color="text.secondary">
              {registration.linkedWasteStreamCount} linked Encamp waste stream
              {registration.linkedWasteStreamCount === 1 ? "" : "s"}
            </Typography>
          </Stack>

          <RegistrationFormFields control={control} disabled={loading} />
        </Stack>
      )}
    </RightDrawerShell>
  );
}
